// Contratos de Colección entre admin, api y web.
//
// La colección agrupa eventos del Core bajo una temática (artista,
// género, periodo, etc.). El admin la edita; minPrice / dateFrom /
// dateTo son agregados que la API calcula en lectura a partir de
// los eventos referenciados (no se persisten).
//
// El `label` es el título trilingüe (lo llamamos label por
// compatibilidad histórica; el copy del producto lo presenta como
// "Título"). El DTO público lo expone ya proyectado al locale como
// `title: string`.
import { z } from 'zod';
import { IdSchema, ImageRefSchema, SlugSchema } from './primitives.js';
import { LOCALES } from './i18n/locales.js';
import { HomeEventPublicSchema } from './home-public.js';

/**
 * Título trilingüe: cada locale es opcional, al menos una traducción
 * obligatoria. Mismo contrato que ChipLabelSchema — sin `default` ni
 * cascada: una colección sin traducción al locale del visitante no
 * se muestra en ese locale.
 */
export const CollectionLabelSchema = z
  .object({
    es: z.string().min(1).optional(),
    ca: z.string().min(1).optional(),
    en: z.string().min(1).optional(),
  })
  .refine((v) => LOCALES.some((l) => typeof v[l] === 'string'), {
    message: 'At least one locale translation is required (es, ca or en)',
  });

/**
 * Descripción trilingüe: todas las traducciones opcionales (la
 * descripción no es obligatoria para la colección).
 */
export const CollectionDescriptionSchema = z.object({
  es: z.string().min(1).optional(),
  ca: z.string().min(1).optional(),
  en: z.string().min(1).optional(),
});

/**
 * Imágenes destacadas de la colección. Dos por la diferencia de
 * ratio entre superficies:
 *  - `page`: retrato 3:4 para la página de colección.
 *  - `thumbnail`: panorámica ~1.97:1 para el bloque de la home.
 * Ambas opcionales; se suben por endpoint propio.
 */
export const CollectionImagesSchema = z.object({
  page: ImageRefSchema.optional(),
  thumbnail: ImageRefSchema.optional(),
});

/**
 * Referencia a un evento dentro de la colección, con su posición
 * (orden manual via drag&drop en el admin).
 */
export const CollectionEventRefSchema = z.object({
  eventId: z.string().min(1),
  position: z.number().int().nonnegative(),
});

/**
 * Una colección está "completa" cuando los tres idiomas tienen
 * título y descripción rellenos. Alimenta el tick global del admin
 * (no es por idioma: o están los tres, o no). El invariante de la
 * home (label trilingüe) se apoya en la parte de label.
 */
export function isCollectionComplete(input: {
  label: CollectionLabel;
  description?: CollectionDescription;
}): boolean {
  const { label, description } = input;
  return LOCALES.every(
    (l) =>
      typeof label[l] === 'string' &&
      typeof description?.[l] === 'string',
  );
}

/**
 * Item de colección que devuelve la API al admin. Incluye los
 * agregados calculados (minPrice / dateFrom / dateTo) — null si la
 * colección está vacía o si no se ha podido resolver ningún evento
 * contra el Core — y el flag `complete` derivado de label+description.
 */
export const CollectionAdminItemSchema = z.object({
  id: IdSchema,
  slug: SlugSchema,
  label: CollectionLabelSchema,
  description: CollectionDescriptionSchema,
  images: CollectionImagesSchema,
  events: z.array(CollectionEventRefSchema),
  complete: z.boolean(),
  minPrice: z.number().nonnegative().nullable(),
  dateFrom: z.string().nullable(),
  dateTo: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  _etag: z.string().optional(),
});

/**
 * Inputs del admin para crear/editar colecciones. El orden de
 * `eventIds` define el orden de la colección (drag&drop en el cliente
 * traduce el reorden a este array antes de enviar). Las imágenes se
 * suben por endpoint propio, no por estos inputs.
 */
export const CreateCollectionInputSchema = z.object({
  slug: SlugSchema,
  label: CollectionLabelSchema,
  description: CollectionDescriptionSchema.optional(),
  eventIds: z.array(z.string().min(1)).default([]),
});

// El slug es inmutable: se omite del update a propósito.
export const UpdateCollectionInputSchema = z
  .object({
    label: CollectionLabelSchema.optional(),
    description: CollectionDescriptionSchema.optional(),
    eventIds: z.array(z.string().min(1)).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, {
    message: 'At least one field must be provided',
  });

/**
 * DTO público de la página de colección. La API proyecta el título
 * y la descripción al locale del visitante (string, sin cascada) y
 * entrega los eventos ya hidratados desde el Core. `image` es la
 * imagen `page` (la home consume su propia imagen vía el endpoint de
 * home). `thumbnailImage` es la imagen `thumbnail` (16:9-ish): la usan
 * consumidores con un layout ancho que no encaja con el hero 3:4 de
 * `image`, como la tarjeta de colección del blog — nunca sustituir
 * `image` por `thumbnail` aquí, rompería la página de colección.
 */
export const CollectionPublicSchema = z.object({
  slug: SlugSchema,
  title: z.string().min(1),
  description: z.string().optional(),
  image: ImageRefSchema.optional(),
  thumbnailImage: ImageRefSchema.optional(),
  // Locales en los que la colección tiene título (para hreflang sin
  // cascada). Siempre incluye al menos el locale solicitado.
  availableLocales: z.array(z.enum(LOCALES)),
  eventCount: z.number().int().nonnegative(),
  dateFrom: z.string().nullable(),
  dateTo: z.string().nullable(),
  minPrice: z.number().nonnegative().nullable(),
  currency: z.string().nullable(),
  events: z.array(HomeEventPublicSchema),
});

/**
 * Query del endpoint público. `locale` es obligatorio: la API
 * proyecta título/descripción a ese idioma o devuelve 404 si no hay
 * título en ese locale (sin cascada).
 */
export const CollectionPublicQuerySchema = z.object({
  locale: z.enum(LOCALES),
});

export type CollectionLabel = z.infer<typeof CollectionLabelSchema>;
export type CollectionDescription = z.infer<typeof CollectionDescriptionSchema>;
export type CollectionImages = z.infer<typeof CollectionImagesSchema>;
export type CollectionEventRef = z.infer<typeof CollectionEventRefSchema>;
export type CollectionAdminItem = z.infer<typeof CollectionAdminItemSchema>;
export type CreateCollectionInput = z.infer<typeof CreateCollectionInputSchema>;
export type UpdateCollectionInput = z.infer<typeof UpdateCollectionInputSchema>;
export type CollectionPublic = z.infer<typeof CollectionPublicSchema>;
export type CollectionPublicQuery = z.infer<typeof CollectionPublicQuerySchema>;

/**
 * Item del listado público de colecciones (para el sitemap). Solo
 * `slug` + los locales con título (hreflang). No resuelve eventos.
 */
export const CollectionListItemSchema = z.object({
  slug: SlugSchema,
  availableLocales: z.array(z.enum(LOCALES)),
});
export const CollectionsListResponseSchema = z.object({
  items: z.array(CollectionListItemSchema),
});
export type CollectionListItem = z.infer<typeof CollectionListItemSchema>;
export type CollectionsListResponse = z.infer<
  typeof CollectionsListResponseSchema
>;
